/**
 * entity-result-cards
 *
 * Per-ENTITY-TYPE renderers for tool-call result cards — distinct from
 * `tool-display-registry` (keyed by TOOL name). A create/modify tool's
 * response carries `{ type, uuid }` (the uuid resolved at DAG egress), so
 * the card can look up a renderer by the entity `type` and resolve the
 * entity's current fields DYNAMICALLY from the in-memory graph store (no
 * baked strings, no re-query — the store is the cache, populated by the DAG
 * subscription). One entity card serves both `create_*` and `modify_*` of
 * that type.
 *
 * This layer is domain-agnostic: domain code registers its cards via
 * `registerEntityResultCard` (e.g. the app registers an AttributeValue card),
 * the same way the streaming layer takes domain link resolvers.
 *
 * FORWARD-EVOLUTION CONTRACT (read before changing what a tool stores).
 * Tool-call responses are immutable historical artifacts — a card may be fed a
 * `summary`/`content` shape captured by an OLDER version of the producing tool,
 * and there is deliberately NO data migration. So every card MUST:
 *   1. DEGRADE, not assume — null-check / validate every field
 *      (`Array.isArray(summary?.rows)`, optional chaining), rendering `null`
 *      when the data it needs isn't present (old or partial captures).
 *   2. NEVER crash — even if (1) is missed, the renderer wraps each card in an
 *      ErrorBoundary that falls back to the generic card, so a bad/old shape
 *      can never take down the chat. Treat the boundary as a backstop, not a
 *      licence to skip (1).
 * Net: change what you capture freely; new cards use the new shape, old cards
 * keep theirs (and degrade), nothing crashes — no migration step.
 */

import React from 'react';

export interface EntityResultCardContext {
  /** Raw tool-response content (`{ type, name, created/modified, ... }`) —
   *  used to pick the card by `type` and as a label fallback. */
  content: any;
  /** The cached structured summary (`{ context, rows }`) from the
   *  ToolCallResponse's `card_summary` field, resolved + cached server-side
   *  at DAG egress. Undefined until computed. */
  summary?: any;
}

export interface EntityResultCard {
  /** Inline header subtitle next to the tool name (e.g. "on: Brushless
   *  Motor"). Render null to fall back to the default entity label. */
  Subtitle?: React.FC<EntityResultCardContext>;
  /** Body region rendered under the header (e.g. "mass_kg = 0.5 kg"). */
  Body?: React.FC<EntityResultCardContext>;
}

const REGISTRY: Record<string, EntityResultCard> = {};

/** Register a per-type result card (idempotent overwrite per type). Called
 *  by domain modules at app load (side-effect import). */
export function registerEntityResultCard(
  entityType: string,
  card: EntityResultCard,
): void {
  REGISTRY[entityType] = card;
}

/** Look up the result card for an entity `type` (the response's `type`
 *  field — e.g. "AttributeValue"). Null when no card is registered. */
export function getEntityResultCard(
  entityType: string | null | undefined,
): EntityResultCard | null {
  if (!entityType) return null;
  return REGISTRY[entityType] ?? null;
}
